import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import PeopleMultiSelect from "./PeopleMultiSelect";
import { createTeam, fetchTeam, updateTeam } from "./team.service";
import { Customer } from "./team.types";

interface Props {
  open: boolean;
  userId: string | null;         // null = add, id = edit
  customerList: Customer[];
  onClose: () => void;
  onSaved: () => void;
}

type Errors = { teamName?: string; description?: string; leaders?: string; members?: string };

export default function TeamFormDrawer({ open, userId, customerList, onClose, onSaved }: Props) {
  const [teamName, setTeamName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [teamLeaders, setTeamLeaders] = React.useState<string[]>([]);
  const [teamMembers, setTeamMembers] = React.useState<string[]>([]);
  const [errors, setErrors] = React.useState<Errors>({});
  const [saving, setSaving] = React.useState(false);

  // Members can't include people already chosen as leaders (same as original)
  const memberOptions = React.useMemo(
    () => customerList.filter((c) => !teamLeaders.includes(c._id)),
    [customerList, teamLeaders]
  );

  // Load on open
  React.useEffect(() => {
    if (!open) return;
    setErrors({});
    if (userId) {
      (async () => {
        try {
          const res = await fetchTeam(userId);
          setTeamName(res?.teamName || "");
          setDescription(res?.description || "");
          // API may return objects or ids — normalize to ids
          setTeamLeaders((res?.teamLeaders || []).map((x: any) => x?._id || x));
          setTeamMembers((res?.teamMembers || []).map((x: any) => x?._id || x));
        } catch {
          toast.error("Unable to load team");
        }
      })();
    } else {
      setTeamName("");
      setDescription("");
      setTeamLeaders([]);
      setTeamMembers([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const validate = (): boolean => {
    const e: Errors = {};
    if (!teamName.trim()) e.teamName = "Team name cannot be empty";
    if (!teamLeaders.length) e.leaders = "At least one team leader is required";
    // members validated against the currently-allowed member options
    const validMembers = teamMembers.filter((id) => memberOptions.some((m) => m._id === id));
    if (!validMembers.length) e.members = "Team must have at least one member";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const validMembers = teamMembers.filter((id) => memberOptions.some((m) => m._id === id));
    const payload = {
      teamName,
      description,
      teamMembers: validMembers,
      teamLeaders,
    };

    try {
      setSaving(true);
      const response = userId
        ? await updateTeam(userId, payload)
        : await createTeam(payload);
      if (response?.data?.success === true) {
        toast.success(response?.data?.message || (userId ? "Team updated successfully" : "Team created successfully"));
        onSaved();
        onClose();
      } else {
        toast.error(userId ? "Unable to update team" : "Unable to create team");
      }
    } catch {
      toast.error(userId ? "Unable to update team" : "Unable to create team");
    } finally {
      setSaving(false);
    }
  };

  const allMembersSelected =
    memberOptions.length > 0 && memberOptions.every((m) => teamMembers.includes(m._id));

  const toggleSelectAllMembers = (checked: boolean) => {
    if (checked) {
      const ids = memberOptions.map((m) => m._id);
      // keep any already-selected that are still valid + all options
      setTeamMembers(Array.from(new Set([...teamMembers.filter((id) => memberOptions.some((m) => m._id === id)), ...ids])));
    } else {
      setTeamMembers([]);
    }
    setErrors((prev) => ({ ...prev, members: undefined }));
  };

  return (
    <>
      <div className={`ctpl-overlay${open ? " open" : ""}`} onClick={onClose} />
      <div className={`ctpl-drawer ctpl-drawer-wide${open ? " open" : ""}`}>
        <div className="ctpl-drawer-header">
          <h2>{userId ? `Edit — ${teamName || "Team"}` : "Create New Team"}</h2>
          <button className="ctpl-icon-btn" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="ctpl-drawer-body">
          <div className="ctpl-field-group">
            <div className="ctpl-section-label">Team info</div>

            <label className="ctpl-field-label">
              Team name <span className="ctpl-required">*</span>
            </label>
            <input
              className={`ctpl-field-input${errors.teamName ? " error" : ""}`}
              placeholder="Enter team name"
              value={teamName}
              onChange={(e) => {
                setTeamName(e.target.value);
                if (e.target.value.trim()) setErrors((p) => ({ ...p, teamName: undefined }));
              }}
            />
            {errors.teamName && <div className="ctpl-field-error">{errors.teamName}</div>}

            <label className="ctpl-field-label" style={{ marginTop: 14 }}>Description</label>
            <textarea
              className="ctpl-field-input"
              placeholder="Enter description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="ctpl-field-group">
            <div className="ctpl-section-label">
              👤 Team leaders <span className="ctpl-required">*</span>
            </div>
            <PeopleMultiSelect
              options={customerList}
              selectedIds={teamLeaders}
              variant="leader"
              placeholder="Search leaders…"
              onChange={(ids) => {
                setTeamLeaders(ids);
                if (ids.length) setErrors((p) => ({ ...p, leaders: undefined }));
                // drop any member that just became a leader
                setTeamMembers((prev) => prev.filter((id) => !ids.includes(id)));
              }}
            />
            {errors.leaders && <div className="ctpl-field-error">{errors.leaders}</div>}
          </div>

          <div className="ctpl-field-group">
            <div className="ctpl-flags-row" style={{ justifyContent: "space-between" }}>
              <div className="ctpl-section-label" style={{ marginBottom: 0 }}>
                👥 Team members <span className="ctpl-required">*</span>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ctpl-ink-soft)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={allMembersSelected}
                  onChange={(e) => toggleSelectAllMembers(e.target.checked)}
                />
                Select all
              </label>
            </div>
            <PeopleMultiSelect
              options={memberOptions}
              selectedIds={teamMembers}
              variant="member"
              placeholder="Search members…"
              onChange={(ids) => {
                setTeamMembers(ids);
                if (ids.length) setErrors((p) => ({ ...p, members: undefined }));
              }}
            />
            {errors.members && <div className="ctpl-field-error">{errors.members}</div>}
          </div>
        </div>

        <div className="ctpl-drawer-footer">
          <button className="ctpl-btn ctpl-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="ctpl-btn ctpl-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : userId ? "Save changes" : "Create team"}
          </button>
        </div>
      </div>
    </>
  );
}